// The install status document — v1.
//
// WHAT THIS IS: a declaration of what `installer/status.mjs` and
// `installer/entrypoint.sh` write TODAY, frozen as v1 of the contract. Every
// field below was read out of the writer, not designed here. Where the shipped
// shape is odd, the oddity is declared and annotated rather than corrected —
// changing what the installer writes is install behaviour, which this lane does
// not touch.
//
// WHERE IT TRAVELS: the installer writes this document to
// `status/status.json` in the per-install storage account, and the CLI, the
// install page and (later) the desktop shell read it. It is the ONLY
// machine-readable account of an install's progress that carries current state.
// The phone-home wire (`install-started` / `-succeeded` / `-failed` on the
// telemetry envelope) is a different transport with different guarantees — see
// `wire.ts` for how the two relate and why neither can substitute for the other.

import {
  arr, bool, enom, lit, map, nullable, num, obj, opt, req, str,
  toJsonSchema, validateNode,
  type SpecNode, type ValidateOptions, type Violation,
} from "./spec.js";

/** The schema version this package declares and validates. */
export const STATUS_SCHEMA_VERSION = 1;

/** Where the installer publishes the document. Paired with `deriveStatusSaName`. */
export const STATUS_CONTAINER = "status";
export const STATUS_BLOB = "status.json";

/** The durable status document the installer writes. */
export interface StatusDoc {
  schemaVersion: number;
  status: "running" | "done" | "failed";
  phase: string;
  phaseIndex: number;
  phaseTotal: number;
  startedAt?: string;
  updatedAt?: string;
  detail?: string;
  result?: StatusResult;
  error?: StatusError;
  history?: StatusHistoryEntry[];
  warnings?: StatusWarning[];
}

/** Written once, at the terminal `done` emit, from entrypoint.sh's `RESULT` jq. */
export interface StatusResult {
  gatewayFqdn?: string;
  gatewayUrl?: string;
  foundryEndpoint?: string;
  freshVsReused?: Record<string, boolean> | null;
  brains?: string[];
  workers?: string[];
  stampSeed?: string;
  updaterProvision?: string;
  platformVersion?: string;
  telemetryIdentity?: string;
  /**
   * Unreleased-debug installs only. Such an install deliberately writes no platform
   * stamp — the stamp is the update rail's identity contract, and an install the
   * platform cannot vouch for belongs outside that rail rather than confusingly
   * inside it. That leaves nothing recording what it actually deployed, which turns
   * diagnosing one into archaeology over container logs, so these carry it instead.
   *
   * FLAT, like every other result field, because the keys are composed in bash and
   * the drift guard that reads them out of the shell models jq objects as flat. A
   * nested object here would have needed that extractor extended; the surrounding
   * shape is flat anyway, so this follows it.
   */
  unreleasedDebugGateway?: string;
  unreleasedDebugExecutor?: string;
  /**
   * `true` when the founder explicitly DECLINED the GitHub step on the install
   * page, so the platform installed without a brain repo. Absent otherwise —
   * including on a brain-less rig run, which reaches the same install shape with
   * no founder behind it and no receipt to write.
   *
   * WHY THIS IS A FIELD AND NOT AN INFERENCE. The finish surface renders a
   * connect-later note, and the only other way to know it should is to read
   * `brains: []` — which is equally true of a rig, and of a hypothetical future
   * path that loses its brain some other way. Deriving a founder-facing sentence
   * from an empty array is the same inference-from-absence this whole path exists
   * to avoid; the install states the fact instead.
   *
   * WHY NOT A WARNING. `warnings[]` surfaces as `running` + `warn` in the event
   * envelope. The skip is a supported choice that is never punished, so
   * reporting it as an advisory would put a yellow mark on a founder for taking
   * a button the product offered them.
   */
  githubDeclined?: boolean;
}

/**
 * The failure, as the LOCAL document records it.
 *
 * `message` is free-form and may carry raw `az` stderr (see `err_detail`'s
 * callers in entrypoint.sh). It is local-status-only and MUST NOT be forwarded
 * to any surface that leaves the founder's subscription — the wire's
 * `errorDetail` is composed separately from an installer-authored, enumerable
 * set precisely so that raw stderr cannot ride outward. A consumer that renders
 * this field renders it to the founder who owns the install, and nowhere else.
 */
export interface StatusError {
  /** Absent when the installer's exit-trap fallback wrote a bare `{}`. */
  phase?: string;
  /** Absent when the installer's exit-trap fallback wrote a bare `{}`. */
  message?: string;
}

export interface StatusHistoryEntry {
  phase: string;
  at: string;
  status: string;
}

/** An advisory a phase raised. Accumulated across the run, never replaced. */
export interface StatusWarning {
  phase: string;
  at: string;
  text: string;
}

/**
 * The field spec — the single source the JSON Schema and the validator are both
 * derived from. Adding a field here and regenerating is the whole ceremony for
 * declaring something new; there is no second place to update.
 */
export const STATUS_DOC_SPEC: SpecNode = obj({
  schemaVersion: req(lit(STATUS_SCHEMA_VERSION), "Always 1 in this version of the contract. A reader that does not recognise the value must refuse the document rather than guess at it."),
  status: req(enom("running", "done", "failed"), "The run's state. `done` and `failed` are terminal; nothing writes the document afterwards."),
  phase: req(str(), "The phase name the installer is in. See the phase registry — the set is open by type but closed in practice, and includes `converge` on the update path."),
  phaseIndex: req(num(), "1-based index of the current phase. NOT monotonic: a restarted container re-runs from phase 1, so this can go backwards mid-install."),
  phaseTotal: req(num(), "Total phases the writer counts, including the terminal `done` emit. Read it from the document, never from a constant — a container built at another commit may report a different total."),
  startedAt: opt(str(), "ISO-8601 UTC. Preserved across a container restart when the prior document was recovered (`hydrate_status`)."),
  updatedAt: opt(str(), "ISO-8601 UTC of this write."),
  detail: opt(str(), "Human-facing one-liner for the current phase. Installer-authored; safe to show, never parse."),
  result: opt(
    obj({
      gatewayFqdn: opt(str()),
      gatewayUrl: opt(str()),
      foundryEndpoint: opt(str()),
      // `entrypoint.sh` fills this with `jq -c '.created'` from `m8t foundry
      // create`'s JSON and passes it through `--argjson`. If that command ever
      // stops carrying `.created`, jq yields the literal `null` and the field
      // lands as null rather than absent — a cross-binary coupling with no
      // guard on either side. Tolerated here so one missing key upstream cannot
      // make an otherwise-good `done` document unreadable.
      freshVsReused: opt(nullable(map(bool())), "Per-resource: true when this run created it, false when it reused an existing one. Null if the installer could not determine it."),
      brains: opt(arr(str())),
      workers: opt(arr(str())),
      stampSeed: opt(str()),
      updaterProvision: opt(str()),
      platformVersion: opt(str(), "The release the engine stamped, e.g. `platform-v0.7.8`. Absent when the engine could not resolve one."),
      telemetryIdentity: opt(str()),
      unreleasedDebugGateway: opt(str(), "Unreleased-debug installs only: the gateway image actually deployed — by tag on the no-manifest path, which is the only one the CLI can produce. Such an install writes no platform stamp on purpose, so this is the only record of what it ran."),
      unreleasedDebugExecutor: opt(str(), "Unreleased-debug installs only: the executor image actually deployed, as passed to the executor deploy. Absent when no executor was deployed."),
      githubDeclined: opt(bool(), "True when the founder explicitly declined the GitHub step, so the platform installed with no brain repo and the finish surface should offer connect-later. ABSENT rather than false otherwise — including on a brain-less rig run, which reaches the same shape with no founder behind it. Read this rather than inferring the same fact from an empty `brains`."),
    }),
    "Present only on a `done` document.",
  ),
  error: opt(
    obj({
      // BOTH optional, and this is not defensive vagueness — the installer's
      // EXIT trap composes this object with jq and falls back to a literal `{}`
      // if that jq fails (`... 2>/dev/null || printf '{}'`). That fallback is
      // the last-resort safety net which guarantees a dead install still
      // publishes a `failed` document instead of freezing on `running`.
      // Requiring these fields would make the reader refuse exactly the
      // document the safety net exists to produce, so a founder whose install
      // died would see a read error instead of which phase died.
      phase: opt(str()),
      message: opt(str(), "Free-form, may carry raw az stderr. LOCAL ONLY — never forward it outward; the wire composes `errorDetail` separately."),
    }),
    "Present only on a `failed` document. May be an EMPTY object when the installer's exit trap could not compose it.",
  ),
  history: opt(
    arr(obj({ phase: req(str()), at: req(str()), status: req(str()) })),
    "Append-only, one entry per emit. Survives a restart via `hydrate_status`, so a resumed run's history contains the earlier attempt's entries too.",
  ),
  warnings: opt(
    arr(obj({ phase: req(str()), at: req(str()), text: req(str()) })),
    "Advisories raised by any phase, accumulated across the run. Written only when at least one exists — an absent key means none were raised, not that the field is unsupported.",
  ),
});

/** The generated JSON Schema for the status document. */
export function statusDocJsonSchema(): Record<string, unknown> {
  return {
    $schema: "https://json-schema.org/draft/2020-12/schema",
    $id: "https://m8t.run/schemas/install/status-doc.v1.json",
    title: "m8t install status document",
    description:
      "The durable status document the m8t installer writes to blob storage during an install. " +
      "Generated from packages/install-contract/src/status-doc.ts — do not hand-edit.",
    ...toJsonSchema(STATUS_DOC_SPEC),
  };
}

/** Why a document was refused. */
export type StatusDocRejection =
  | { reason: "not-json"; message: string }
  /** No `schemaVersion` key at all — a truncated or corrupt blob, not a newer writer. */
  | { reason: "missing-schema-version" }
  | { reason: "unknown-schema-version"; found: unknown; supported: number }
  | { reason: "invalid"; violations: Violation[] };

export type StatusDocParse =
  | { ok: true; doc: StatusDoc }
  | { ok: false; rejection: StatusDocRejection };

/**
 * Parse and validate a status document.
 *
 * UNKNOWN VERSIONS ARE REFUSED, LOUDLY. A document declaring a schemaVersion
 * this reader does not know is not partially read, not best-effort rendered, and
 * not silently treated as v1 — it is rejected with the version it found. The
 * alternative is a reader that guesses, and a progress surface that guesses about
 * an install is worse than one that says it cannot tell: the founder is watching
 * a bar that has no idea what it is drawing.
 *
 * This is the contract's forward-compatibility rule, and it is deliberately
 * asymmetric — a NEWER document is refused by an OLDER reader (which is right;
 * the old reader genuinely cannot know what changed), and the way to ship a
 * breaking change is therefore to roll readers before writers.
 */
export function parseStatusDoc(raw: string, opts: ValidateOptions = {}): StatusDocParse {
  let parsed: unknown;
  try {
    // The message from a JSON parse failure can quote a fragment of the input.
    // That is acceptable HERE and only here: this document lives in the
    // founder's own subscription and is read on the founder's own machine, so
    // the fragment goes back to the person who already owns it. It must not be
    // forwarded anywhere the document itself would not go.
    parsed = JSON.parse(raw.trim());
  } catch (e) {
    return { ok: false, rejection: { reason: "not-json", message: (e as Error).message } };
  }
  const hasVersion =
    typeof parsed === "object" && parsed !== null &&
    Object.prototype.hasOwnProperty.call(parsed, "schemaVersion");
  if (!hasVersion) {
    // Every version of the writer that has ever existed hardcodes
    // `schemaVersion: 1`, so an absent one means a truncated or corrupt blob —
    // NOT a newer installer. Kept distinct because the remedies are opposites:
    // this one is not fixed by updating the reader.
    return { ok: false, rejection: { reason: "missing-schema-version" } };
  }
  const version = (parsed as { schemaVersion?: unknown }).schemaVersion;
  if (version !== STATUS_SCHEMA_VERSION) {
    return { ok: false, rejection: { reason: "unknown-schema-version", found: version, supported: STATUS_SCHEMA_VERSION } };
  }
  const violations = validateNode(STATUS_DOC_SPEC, parsed, "", opts);
  if (violations.length > 0) return { ok: false, rejection: { reason: "invalid", violations } };
  return { ok: true, doc: parsed as StatusDoc };
}

/**
 * Parse the way a RUNTIME READER should: tolerant of fields it does not know.
 *
 * A CLI in a founder's hands must not refuse a document because a newer
 * installer added a field — that would make every additive change breaking for
 * everyone already installed. Strictness belongs in CI, where the drift test
 * fails a developer instead.
 */
export function parseStatusDocForReader(raw: string): StatusDocParse {
  return parseStatusDoc(raw, { unknownFields: "ignore" });
}

/** How many violations `explainRejection` spells out before summarising the rest. */
const MAX_EXPLAINED_VIOLATIONS = 3;

/** A one-line, founder-safe explanation of a rejection. */
export function explainRejection(r: StatusDocRejection): string {
  switch (r.reason) {
    case "not-json":
      return `the installer status document is not valid JSON (${r.message})`;
    case "missing-schema-version":
      return "the installer status document has no schemaVersion, so it is truncated or corrupt rather than simply newer";
    case "unknown-schema-version":
      return (
        `the installer status document declares schemaVersion ${JSON.stringify(r.found)}, ` +
        `but this build only understands version ${String(r.supported)}. Update the tool that is reading it.`
      );
    case "invalid": {
      // A badly drifted document can violate on every field; spelling all of
      // them out turns one CLI error into a wall of text.
      const shown = r.violations.slice(0, MAX_EXPLAINED_VIOLATIONS).map((v) => `${v.path} ${v.message}`);
      const rest = r.violations.length - shown.length;
      return (
        `the installer status document does not match the contract: ` +
        shown.join("; ") + (rest > 0 ? ` (and ${String(rest)} more)` : "")
      );
    }
  }
}
